import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { animate } from "framer-motion";
import { AlertTriangle, Banknote, Bell, BarChart3, Calculator, CalendarClock, Crown, Landmark, MoreHorizontal, ReceiptText, RefreshCw, Repeat, User } from "lucide-react";
import { formatEUR, formatEUR0 } from "@/lib/format";
import { LiqoMark } from "@/components/LiqoMark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const menuItemClass =
  "flex cursor-pointer items-center gap-2.5 rounded-[6px] px-2 py-2 text-[12px] font-light text-white/80 focus:bg-white/[0.06] focus:text-white data-[highlighted]:bg-white/[0.06]";

const StatusClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <span className="mono text-[10px] tracking-[0.15em] text-[#00FF87]" data-testid="status-clock">
      {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}
    </span>
  );
};

const UpdatedAgo = ({ ts }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  if (!ts) return null;
  const mins = Math.round((Date.now() - ts) / 60000);
  return (
    <p className="mono mt-1 text-[10px] tracking-[0.08em] text-white/30">
      {mins < 1 ? "Aggiornato ora" : `Aggiornato ${mins} min fa`}
    </p>
  );
};

export const BalanceHeader = ({ balance, onRefresh, refreshing, lastUpdated, onBank, bankConnected, onAnalytics, onTax, onSubscriptions, onNotifications, notifUnread = 0, onInvestments, onMonth, onMonthly, onAccount, isFounder = false }) => {
  const navigate = useNavigate();
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  const investments = balance?.investments || 0;
  const hasInvest = investments > 0;
  const netWorth = balance ? (balance.net_worth ?? balance.balance) : null;

  // count-up 800ms ease-out (mostra il patrimonio netto se ci sono investimenti)
  useEffect(() => {
    if (netWorth == null) return;
    const controls = animate(prev.current, netWorth, {
      duration: 0.8,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v),
    });
    prev.current = netWorth;
    return () => controls.stop();
  }, [netWorth]);

  const monthNet = balance ? balance.month_income - balance.month_spent : 0;
  const up = monthNet >= 0;

  return (
    <div className="sticky top-0 z-20 border-b border-white/[0.04] bg-[rgba(5,5,8,0.85)] backdrop-blur-[20px]">
      {/* barra di stato */}
      <div className="flex items-center justify-between border-b border-white/[0.04] px-4 py-1.5">
        <StatusClock />
        <div className="flex items-center gap-2.5">
          <span className="flex items-center gap-1.5">
            <LiqoMark size={15} />
            <span className="mono text-[10px] tracking-[0.3em] text-white/35">
              LIQO<span className="text-[#00FF87]">_</span>
            </span>
          </span>
          {isFounder && (
            <span
              data-testid="founder-badge"
              className="mono flex items-center gap-1 rounded-[4px] border px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-[0.18em]"
              style={{
                borderColor: "rgba(0,255,135,0.45)",
                color: "#00FF87",
                background: "rgba(0,255,135,0.06)",
                boxShadow: "0 0 12px rgba(0,255,135,0.18)",
              }}
            >
              <Crown size={9} /> Founder
            </span>
          )}
          <button
            onClick={onAccount}
            data-testid="home-account-button"
            aria-label="Account"
            className="flex h-5 w-5 items-center justify-center rounded-[5px] border border-white/[0.1] text-white/50 hover:border-[#00FF87]/40 hover:text-[#00FF87] hover:bg-white/[0.05] active:scale-[0.95]"
          >
            <User size={11} />
          </button>
        </div>
      </div>

      <div className="px-4 pb-4 pt-3">
        <div className="flex items-center justify-between">
          <p className="caps-tag text-white/40">{hasInvest ? "Patrimonio netto" : "Saldo netto"}</p>
          <div className="flex items-center gap-1.5">
            {/* Azioni principali: Notifiche · Banca · Aggiorna */}
            <button
              onClick={onNotifications}
              data-testid="home-notifications-button"
              aria-label="Notifiche"
              className="relative flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
            >
              <Bell size={14} />
              {notifUnread > 0 && (
                <span
                  className="mono absolute -right-1 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-[#FF4D4D] px-1 text-[9px] font-semibold leading-none text-black"
                  data-testid="notifications-badge"
                >
                  {notifUnread > 9 ? "9+" : notifUnread}
                </span>
              )}
            </button>
            <button
              onClick={onBank}
              data-testid="home-bank-button"
              aria-label="Banca"
              className={`relative flex h-8 w-8 items-center justify-center rounded-[6px] border text-white/55 hover:bg-white/[0.05] ${
                bankConnected ? "border-[#00FF87]/30 text-[#00FF87]" : "border-white/[0.08]"
              }`}
            >
              <Landmark size={14} />
              {bankConnected && (
                <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#00FF87] shadow-[0_0_6px_rgba(0,255,135,0.8)]" />
              )}
            </button>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="home-refresh-button"
              aria-label="Aggiorna insight"
              className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05] disabled:opacity-50"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            </button>

            {/* Menu "•••": strumenti secondari */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="home-menu-button"
                  aria-label="Altre azioni"
                  className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05] data-[state=open]:border-[#00FF87]/40 data-[state=open]:text-[#00FF87]"
                >
                  <MoreHorizontal size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="z-[200] w-56 rounded-[10px] border-white/[0.08] bg-[#10101A]/95 p-1.5 text-white shadow-[0_12px_40px_rgba(0,0,0,0.6)] backdrop-blur-xl"
                data-testid="home-menu-content"
              >
                <DropdownMenuLabel className="caps-tag px-2 py-1.5 !text-white/35">Strumenti</DropdownMenuLabel>
                <DropdownMenuItem onClick={onAnalytics} data-testid="menu-analytics" className={menuItemClass}>
                  <BarChart3 size={14} className="text-white/55" /> Analytics
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onTax} data-testid="menu-tax" className={menuItemClass}>
                  <Calculator size={14} className="text-[#F5A623]" /> Fisco &amp; tasse
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSubscriptions} data-testid="menu-subscriptions" className={menuItemClass}>
                  <Repeat size={14} className="text-[#FF7AB6]" /> Abbonamenti &amp; spese fisse
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1 bg-white/[0.06]" />
                <DropdownMenuItem
                  onClick={() => navigate("/transactions")}
                  data-testid="menu-transactions"
                  className={menuItemClass}
                >
                  <ReceiptText size={14} className="text-white/55" /> Transazioni
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/transactions?add=cash")}
                  data-testid="menu-add-cash"
                  className={menuItemClass}
                >
                  <Banknote size={14} className="text-[#00FF87]" /> Aggiungi contanti
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <p
          className="mono mt-2 text-[52px] font-light leading-none tracking-[-2px] text-[#00FF87] tabular"
          data-testid="home-net-balance"
        >
          {balance ? formatEUR(display) : "—"}
        </p>

        {balance?.bank_stale && (
          <button
            onClick={onBank}
            data-testid="home-bank-stale-hint"
            className="mono mt-2.5 flex items-center gap-1.5 rounded-[5px] border border-[#F5A623]/35 bg-[#F5A623]/[0.06] px-2 py-1 text-[10px] tracking-[0.04em] text-[#F5A623] hover:bg-[#F5A623]/[0.12] active:scale-[0.98]"
          >
            <AlertTriangle size={11} /> Saldo non riconciliato · ricollega la banca
          </button>
        )}

        {balance && (
          <>
            <p className="mono mt-3 text-xs tracking-[0.05em]" data-testid="home-month-summary">
              <span className={`liqo-arrow-anim mr-1 ${up ? "text-[#00FF87]" : "text-[#FF4D4D]"}`}>
                {up ? "↑" : "↓"}
              </span>
              <span className={up ? "text-[#00FF87]" : "text-[#FF4D4D]"}>
                {up ? "+" : "−"}{formatEUR0(Math.abs(monthNet))}
              </span>
              <span className="text-white/40"> questo mese</span>
            </p>
            <UpdatedAgo ts={lastUpdated} />

            <button
              onClick={onInvestments}
              data-testid="home-investments-entry"
              className="mono mt-2 flex w-full items-center gap-1.5 text-left text-[11px] tracking-[0.04em] text-white/40 hover:text-white/70"
            >
              {hasInvest ? (
                <>
                  <span>Liquidità {formatEUR0(balance.balance)}</span>
                  <span className="text-white/20">·</span>
                  <span style={{ color: "#2DD4BF" }}>Investimenti {formatEUR0(investments)}</span>
                  <span style={{ color: "#2DD4BF" }}>↗</span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1" style={{ color: "#2DD4BF", opacity: 0.85 }}>
                  ↗ Aggiungi investimenti
                </span>
              )}
            </button>

            <button
              onClick={onMonth}
              data-testid="home-month-entry"
              className="mono mt-1.5 flex w-full items-center gap-1.5 text-left text-[11px] tracking-[0.04em] text-white/40 hover:text-white/70"
            >
              <CalendarClock size={12} style={{ color: "#00D4FF" }} />
              <span>Questo mese · entrate &amp; uscite</span>
            </button>

            <button
              onClick={onMonthly}
              data-testid="home-monthly-entry"
              className="mono mt-1.5 flex w-full items-center gap-1.5 text-left text-[11px] tracking-[0.04em] text-white/40 hover:text-white/70"
            >
              <span style={{ color: "#A78BFA" }}>Σ</span>
              <span>Report mensile · risparmio &amp; piano</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
